import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import {
  createEditor,
  Descendant,
  Editor,
  Element,
  Location,
  NodeEntry,
  Range,
  Node as SlateNode, // Not to be confused with native "Node" class
  Text,
  Transforms,
} from "slate";
import { Editable, ReactEditor, Slate, withReact } from "slate-react";
import { colours } from "../../constants/colours";
import { Portal } from "../../Portal";
import { Skeleton } from "../../Skeleton";
import { mergeProps } from "../../utils/component-props";
import { usePrevious } from "../../utils/hooks/usePrevious";
import { InputWithLabels } from "../_components/InputWithLabels";
import textInputStyles from "../TextInput/TextInput.module.scss";
import type {
  ControlledVariableTextInputProps,
  HighlightedRange,
  HighlightedRenderLeafProps,
  ParagraphElement,
  SlateEditorType,
  VariableTextInputProps,
} from "./types";
import styles from "./VariableTextInput.module.scss";

const textInputCx = classNames.bind(textInputStyles);
const cx = classNames.bind(styles);

/**
 * In Slate, a "leaf" is a segment of text with the same formatting (a `bold`, `italic`, custom `span` etc).
 * Leaves are the smallest text units and are rendered using the `renderLeaf` prop in `<Editable />`.
 * */
const CustomLeaf = ({
  attributes,
  children,
  leaf,
}: HighlightedRenderLeafProps) => {
  if (leaf.metadata?.highlight) {
    return (
      <span
        {...attributes}
        style={{ color: leaf.metadata?.color }}
      >
        {children}
      </span>
    );
  }
  return <span {...attributes}>{children}</span>;
};

const extractLastBlockFromEditor = (editorInstance: SlateEditorType) => {
  const blockInstance = Editor.above(editorInstance, {
    match: (n) => Element.isElement(n) && Editor.isBlock(editorInstance, n),
  });
  let text = "";
  if (blockInstance && editorInstance.selection) {
    const blockRange = {
      anchor: Editor.start(editorInstance, blockInstance[1]),
      focus: editorInstance.selection.anchor,
    };
    /** All the text extracted from previous block */
    text = Editor.string(editorInstance, blockRange);
  }
  return { text, blockInstance };
};

/**
 * A text input component that supports highlighting text using Regex and dropdown suggestions
 */
const VariableTextInput = ({
  value,
  onChange,
  placeholder,
  colorOptions = {
    regexDefaultHighlightColor: colours.status.warning,
    nonSuggestionHighlightColor: colours.status.error,
  },
  highlightRegex = /\$[A-Z]+[A-Z_0-9]+/g,
  suggestions,
  shouldHighlightNonSuggestion = true,
  suggestionsTriggerRegex = /\$[A-Z_0-9]*$/i,
  leftSlot,
  flairSlot,
  isBusy,
  isLoading,
  isDisabled,
  disabled,
  error,
  invalidMessage,
  "aria-invalid": ariaInvalid,
  type = "text",
  style,
}: VariableTextInputProps) => {
  const previousValue = usePrevious(value);

  const plainTextValue = value == null ? "" : String(value);

  // Editor instance
  const editor = useMemo(() => withReact(createEditor()), []);

  // initial value needed so there's at least one node in the editor state:
  const initialValue = useMemo(
    () => [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ],
    []
  );

  // Reference to dropdown element
  const dropdownElementRef = useRef<HTMLDivElement | null>(null);

  // Whether or not suggestion dropdown should be shown
  const [showDropdown, setShowDropdown] = useState(false);

  // Filtered suggestions based on what user has typed
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>(
    suggestions ?? []
  );

  // Used for keeping the index of selected suggestion using arrow keys
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);

  /**
   * In Slate, any change to the editor will trigger onChange and onValueChange.
   * We need to prevent onChange from being triggered when setting the value through props
   */
  const disableOnChangeTrigger = useRef(false);

  /**
   * Track when value changes come from internal user input vs external prop changes
   */
  const isInternalChange = useRef(false);

  /**
   * Checks if user's NOT selecting any text using `Range.isCollapsed`.
   * In other words, checks if the cursor (caret) is sitting idley
   */
  const isCursorIdleAndPresent = useCallback(
    (
      selection: SlateEditorType["selection"]
    ): selection is NonNullable<SlateEditorType["selection"]> => {
      return Boolean(selection && Range.isCollapsed(selection));
    },
    [editor]
  );

  /**
   * Check if dropdown should appear after cursor moves or user types
   */
  const handleDropdownAfterTyping = () => {
    const { selection } = editor;
    if (!suggestions) return;

    if (!isCursorIdleAndPresent(selection)) {
      setShowDropdown(false);
      return;
    }

    // Extracting the nearest block-level element above the cursor

    const { text: previousBlockText } = extractLastBlockFromEditor(editor);

    // Check the text before cursor and see if the dropdown should appear:
    const match = previousBlockText.match(suggestionsTriggerRegex ?? /.*$/i);

    if (!match) {
      setShowDropdown(false);
      return;
    }

    const filteredSuggestions = suggestions.filter((v) =>
      v.toLocaleLowerCase().startsWith(match[0].toLocaleLowerCase())
    );

    // Suggestion dropdown state:
    setFilteredSuggestions(filteredSuggestions);
    setShowDropdown(filteredSuggestions.length > 0);
    setSelectedSuggestionIndex(0);

    // Try to find the exact position of the cursor in the document
    // and set the exact position of the dropmenu to it:
    setTimeout(() => {
      try {
        const domRange = ReactEditor.toDOMRange(editor, selection);
        const currentCursorPosition = domRange.getBoundingClientRect();
        if (dropdownElementRef.current && currentCursorPosition) {
          dropdownElementRef.current.style.top = `${currentCursorPosition.top + 15}px`;
          dropdownElementRef.current.style.left = `${currentCursorPosition.left}px`;
        }
      } catch (_) {
        // noop
      }
    }, 1);
  };

  /**
   * A handler that listens to all changes in the editor (including cursor moving and typing).
   */
  const onChangeHandler = (/* value */) => {
    handleDropdownAfterTyping();
  };

  /**
   * A handler that only listens to actual text changes in the editor, not the cursor.
   */
  const onValueChange = (newValue: Descendant[]) => {
    /**
     * An unnecessary change trigger that's caused by changes in `value`
     */
    if (disableOnChangeTrigger.current) {
      disableOnChangeTrigger.current = false;
      return;
    }

    // Mark this as an internal change from user input
    isInternalChange.current = true;

    // For text type, join with space to maintain single line
    // For textarea type, join with newlines to preserve line breaks
    const separator = type === "text" ? " " : "\n";
    const plainText = newValue.map((n) => SlateNode.string(n)).join(separator);
    onChange?.(plainText);
  };

  /**
   * Replaces a matching range with text from suggestions list
   */
  const replaceSuggestionInCursorPosition = (textToReplace: string) => {
    if (textToReplace) {
      const { selection } = editor;
      if (isCursorIdleAndPresent(selection)) {
        const { blockInstance: block, text: previousBlockText } =
          extractLastBlockFromEditor(editor);

        let match: RegExpMatchArray | null = null;
        let blockRange: Location | null = null;
        if (block) {
          blockRange = {
            anchor: Editor.start(editor, block[1]),
            focus: selection.anchor,
          };
          match = previousBlockText.match(suggestionsTriggerRegex ?? /.*$/i);
        }
        if (match && blockRange && block?.[1]) {
          const matchStart = match.index ?? 0;
          const matchLength = match[0].length;
          const startPoint = Editor.start(editor, block[1]);
          const matchAnchor = { path: startPoint.path, offset: matchStart };
          const matchFocus = {
            path: startPoint.path,
            offset: matchStart + matchLength,
          };
          Transforms.select(editor, {
            anchor: matchAnchor,
            focus: matchFocus,
          });
          Transforms.insertText(editor, textToReplace);
          Transforms.collapse(editor, { edge: "end" });

          // Manually trigger onValueChange with the updated value
          onValueChange(editor.children);
        }
      }
      setShowDropdown(false);
    }
  };

  /**
   * Listens to arrow up, arrow down, enter (or return) on the suggestion dropdown
   */
  const onKeyDown = (event: React.KeyboardEvent) => {
    // Prevent Enter key when:
    // 1. Type is "text" (to prevent line breaks)
    // 2. Dropdown is open (to allow suggestion selection)
    if (event.key === "Enter" && (type === "text" || showDropdown)) {
      event.preventDefault();
    }
    if (!showDropdown || filteredSuggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (event.key === "Enter" || event.key === "Return") {
      const textToReplace = filteredSuggestions[selectedSuggestionIndex];

      replaceSuggestionInCursorPosition(textToReplace);
    }
  };

  /**
   * "decorate" function of Slate is responsible for modifying a node.
   * This function wraps each highlight match in its own colored `span`
   */
  const decorate: ((entry: NodeEntry) => HighlightedRange[]) | undefined =
    useCallback(
      ([node, path]) => {
        const ranges: HighlightedRange[] = [];
        if (!Text.isText(node)) return [];
        if (!highlightRegex) return [];

        const { text } = node;

        let match: RegExpExecArray | null = null;
        while ((match = highlightRegex.exec(text)) !== null) {
          const matchedHighlight = match[0];

          let color = colorOptions?.regexDefaultHighlightColor; // Default color
          if (
            colorOptions?.regexCustomHighlightColorMapping?.[matchedHighlight]
          )
            color =
              colorOptions.regexCustomHighlightColorMapping[matchedHighlight];

          if (
            suggestions &&
            !suggestions.includes(matchedHighlight) &&
            shouldHighlightNonSuggestion &&
            colorOptions?.nonSuggestionHighlightColor
          ) {
            color = colorOptions.nonSuggestionHighlightColor;
          }

          ranges.push({
            anchor: { path, offset: match.index },
            focus: { path, offset: match.index + matchedHighlight.length },
            metadata: {
              highlight: true,
              color,
            },
          });
        }
        return ranges;
      },
      [
        JSON.stringify({
          suggestions,
          colorOptions,
          shouldHighlightNonSuggestion,
          highlightRegex: highlightRegex?.toString() ?? "",
        }),
      ]
    );

  /**
   * An effect to set reset the state of Slate when `value` changes.
   */
  useEffect(() => {
    const previousPlain = previousValue == null ? "" : String(previousValue);
    if (previousPlain === plainTextValue) return;

    // If this is an internal change from user input, don't update the editor
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    // Store current selection to restore it after content update
    const currentSelection = editor.selection;

    disableOnChangeTrigger.current = true;
    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });

    // Remove empty node
    Transforms.removeNodes(editor, {
      at: [0],
    });

    if (type === "text") {
      // For text type, create a single paragraph with the value
      Transforms.insertNodes(editor, [
        {
          type: "paragraph",
          children: [{ text: plainTextValue }],
        } as ParagraphElement,
      ]);
    } else {
      // For textarea type, split by newlines and create multiple paragraphs
      const lines = plainTextValue.split("\n");
      const paragraphNodes = lines.map(
        (line) =>
          ({
            type: "paragraph",
            children: [{ text: line }],
          }) as ParagraphElement
      );

      Transforms.insertNodes(editor, paragraphNodes);
    }

    // Try to restore cursor position if it was valid
    if (currentSelection && Range.isCollapsed(currentSelection)) {
      try {
        // Ensure the path is still valid after content changes
        const point = currentSelection.anchor;
        const nodeAtPath = editor.children[point.path[0]] ?? editor.children[0];
        const maxOffset = nodeAtPath ? SlateNode.string(nodeAtPath).length : 0;
        const safeOffset = Math.min(point.offset, maxOffset);

        Transforms.select(editor, {
          anchor: { path: point.path, offset: safeOffset },
          focus: { path: point.path, offset: safeOffset },
        });
      } catch {
        // If restoring selection fails, place cursor at end
        Transforms.select(editor, Editor.end(editor, []));
      }
    }
  }, [plainTextValue, previousValue, type]);

  /**
   * Make sure to close the suggestion dropdown when clicking outside of the dropdown.
   */
  useEffect(() => {
    const click = (e: MouseEvent) => {
      if (e.target !== dropdownElementRef.current) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", click);
    return () => {
      document.removeEventListener("click", click);
    };
  }, []);

  const isLoadingOrBusy = !!isLoading || !!isBusy;

  // Calculate dynamic styles based on type
  const editableStyles = useMemo((): React.CSSProperties => {
    return {
      whiteSpace: type === "text" ? "pre" : "pre-wrap",
    };
  }, [type]);

  return (
    <>
      <Slate
        editor={editor}
        initialValue={initialValue}
        onValueChange={onValueChange}
        onChange={onChangeHandler}
      >
        {/* Skeleton should be around Editable, not Slate */}
        <Skeleton
          className={cx(
            "skeletonWrapper",
            type === "text" && "textType",
            type === "textarea" && "textareaType"
          )}
          isLoading={isLoadingOrBusy}
        >
          <div className={textInputCx("container")}>
            {leftSlot && (
              <div className={textInputCx("left-slot")}>{leftSlot({})}</div>
            )}
            <div className={textInputCx("inputContainer")}>
              <Editable
                decorate={decorate}
                renderLeaf={(props) => <CustomLeaf {...props} />}
                placeholder={placeholder}
                readOnly={isDisabled || disabled}
                disabled={isDisabled || disabled}
                className={`${textInputCx("input", {
                  "input--invalid":
                    ariaInvalid === true || !!invalidMessage || !!error,
                  "input--disabled": isDisabled || disabled,
                  "input--with-left-adornment": !!leftSlot,
                })} ${cx("editable", {
                  textType: type === "text",
                  textareaType: type === "textarea",
                })}`}
                onKeyDown={onKeyDown}
                style={{ ...editableStyles, ...style }}
              />
              {flairSlot && (
                <div className={textInputCx("flair")}>{flairSlot({})}</div>
              )}
            </div>
          </div>
        </Skeleton>
      </Slate>
      {!isLoadingOrBusy && showDropdown && filteredSuggestions.length > 0 && (
        <Portal>
          <div
            ref={dropdownElementRef}
            className={cx("dropdownMenu")}
            {...(suggestions ? { "aria-expanded": showDropdown } : {})}
          >
            {filteredSuggestions.map((textToReplace, i) => (
              <div
                key={textToReplace}
                className={cx(
                  "menuItem",
                  i === selectedSuggestionIndex && "selected"
                )}
                onClick={(event) => {
                  event.preventDefault();
                  replaceSuggestionInCursorPosition(textToReplace);
                  setTimeout(() => {
                    ReactEditor.focus(editor);
                  }, 1);
                }}
                onMouseEnter={() => setSelectedSuggestionIndex(i)}
              >
                {textToReplace}
              </div>
            ))}
          </div>
        </Portal>
      )}
    </>
  );
};

export const ControlledVariableTextInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledVariableTextInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "Variable input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
    });
  }, [restProps, field.onChange, field.onBlur]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, labelId, inputId, leftSlot, flairSlot }) => (
        <VariableTextInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          leftSlot={leftSlot}
          flairSlot={flairSlot}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          isInline={isInline}
          isFluid={isFluid}
          {...field}
          {...mergedProps}
        />
      )}
    </InputWithLabels>
  );
};

export default VariableTextInput;
